import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "@mui/material";
import {
  Download,
  FileText,
  Users,
  Activity,
  ArrowLeft,
  Search,
  Download as ExportIcon,
  X,
} from "lucide-react";
import "../style/admintracking.css";
import { AbstractAPI } from "../api/abstract";

/* ================= Helpers ================= */
const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const exportCSV = (rows, columns, filename) => {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => `"${(r[c.key] ?? "").toString().replace(/"/g, '""')}"`)
        .join(","),
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};
const endpointLabelMap = {
  "/api/abstract/all/abstract": "User Dashboard",
  "/api/auth/login/": "Login Page",
  "/": "Main Screen",
  "/api/auth/register/": "Register",
  "/api/verify/": "OTP Verification",
  "/api/auth/refresh/": "Page Refresh",
};
const statusLabel = {
  200: "Success",
  500: "Error",
};
/* ================= Column Definitions ================= */
const COLUMNS = {
  downloads: [
    { key: "user_email", label: "User Email" },
    { key: "file_title", label: "File Title" },
    { key: "download_type", label: "Type" },
    { key: "downloaded_at", label: "Date", render: fmt },
    { key: "ip_address", label: "IP Address" },
  ],
  logins: [
    { key: "user_email", label: "User Email" },
    { key: "user_name", label: "Name" },
    { key: "ip_address", label: "IP Address" },
    { key: "user_agent", label: "User Agent" },
    { key: "login_time", label: "Login Time", render: fmt },
    { key: "status", label: "Status" },
  ],

  traffic: [
    { key: "user_email", label: "User Email" },
    { key: "ip_address", label: "IP Address" },
    {
      key: "endpoint",
      label: "Endpoint",
      render: (val) => endpointLabelMap[val] || val,
    },
    { key: "method", label: "Method" },
    {
      key: "status_code",
      label: "Status Code",
      render: (val) => statusLabel[val] || val,
    },
    { key: "response_time_ms", label: "Response Time (ms)" },
    { key: "timestamp", label: "Timestamp", render: fmt },
  ],
  Registrations: [
  { key: "email", label: "Email" },
  {
    key: "name",
    label: "Name",
    render: (_, row) =>
      row.first_name || row.last_name
        ? `${row.first_name} ${row.last_name}`.trim()
        : row.applicant_name || "—",
  },
  { key: "applicant_phone", label: "Phone" },
  { key: "organisation_name", label: "Organisation" },
  { key: "designation", label: "Designation" },
],
};

/* ================= Active Users Modal ================= */
const ActiveUsersModal = ({ users, count, since, onClose }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="active-users-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2>
          <Activity size={20} /> Active Users —{" "}
          <span className="badge">{count}</span>
        </h2>
        <button className="close-modal-btn" onClick={onClose}>
          <X size={18} />
        </button>
      </div>

      {since && <p className="modal-since">Active since: {fmt(since)}</p>}

      {users.length === 0 ? (
        <p className="modal-empty">No active users in this window.</p>
      ) : (
        <div className="active-users-table-wrap">
          <table className="log-table">
            <thead>
              <tr>
                <th>#</th>
                <th>ID</th>
                <th>Email</th>
                <th>Name</th>
                <th>Organisation</th>
                <th>Designation</th>
                <th>Role</th>
                <th>Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id}>
                  <td>{i + 1}</td>
                  <td>{u.id}</td>
                  <td>{u.email}</td>
                  <td>
                    {u.first_name || u.last_name
                      ? `${u.first_name} ${u.last_name}`.trim()
                      : u.applicant_name || "—"}
                  </td>
                  <td>{u.organisation_name || "—"}</td>
                  <td>{u.designation || "—"}</td>
                  <td>
                    <span className={`role-badge role-${u.user_role}`}>
                      {u.user_role}
                    </span>
                  </td>
                  <td>{fmt(u.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  </div>
);

/* ================= Date Filter Bar ================= */
const DateRangeFilter = ({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onClear,
}) => (
  <div className="date-filter-bar">
    <div className="date-filter-group">
      <label>From</label>
      <input
        type="date"
        value={fromDate}
        max={toDate || undefined}
        onChange={(e) => onFromChange(e.target.value)}
        className="date-input"
      />
    </div>
    <div className="date-filter-group">
      <label>To</label>
      <input
        type="date"
        value={toDate}
        min={fromDate || undefined}
        onChange={(e) => onToChange(e.target.value)}
        className="date-input"
      />
    </div>
    {(fromDate || toDate) && (
      <button className="clear-date-btn" onClick={onClear}>
        ✕ Clear
      </button>
    )}
  </div>
);

/* ================= Main Component ================= */
export default function AdminTracking() {
  const navigate = useNavigate();

  // ── Tab & UI State ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("downloads");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  // ── Date Range State ────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── Downloads ───────────────────────────────────────────────────────────────
  const [downloadLogs, setDownloadLogs] = useState([]);
  const [downloadCount, setDownloadCount] = useState(0);
  const [downloadPage, setDownloadPage] = useState(1);
  const [RegistrationPage, setRegistrationPage] = useState(1);
  const [downloadLoading, setDownloadLoading] = useState(false);
const [registrationLogs, setRegistrationLogs] = useState([]);
// const [registrationCount, setRegistrationCount] = useState(0);
const [registrationLoading, setRegistrationLoading] = useState(false);
  
const fetchDownloadLogs = useCallback(async (pg = 1, from = "", to = "") => {
    setDownloadLoading(true);
    try {
      const data = await AbstractAPI.getDownloadLogs(pg, PER_PAGE, from, to);
      setDownloadLogs(data.results ?? []);
      setDownloadCount(data.count ?? 0);
    } catch (err) {
      console.error("Download logs fetch error:", err);
    } finally {
      setDownloadLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Login Logs ──────────────────────────────────────────────────────────────
  const [loginLogs, setLoginLogs] = useState([]);
  const [loginCount, setLoginCount] = useState(0);
  const [loginPage, setLoginPage] = useState(1);
  const [loginLoading, setLoginLoading] = useState(false);

  const fetchLoginLogs = useCallback(async (pg = 1, from = "", to = "") => {
    setLoginLoading(true);
    try {
      const data = await AbstractAPI.getLoginLogs(pg, PER_PAGE, from, to);
      setLoginLogs(data.results ?? []);
      setLoginCount(data.count ?? 0);
    } catch (err) {
      console.error("Login logs fetch error:", err);
    } finally {
      setLoginLoading(false);
    }
  }, []);

  // ── Traffic Logs ────────────────────────────────────────────────────────────
  const [trafficLogs, setTrafficLogs] = useState([]);
  const [trafficCount, setTrafficCount] = useState(0);
  const [registrationCount, setRegistrationCount] = useState(0);
  const [trafficPage, setTrafficPage] = useState(1);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  const fetchTrafficLogs = useCallback(async (pg = 1, from = "", to = "") => {
    setTrafficLoading(true);
    try {
      const data = await AbstractAPI.getTrafficLogs(pg, PER_PAGE, from, to);
      setTrafficLogs(data.results ?? []);
      setTrafficCount(data.count ?? 0);
    } catch (err) {
      console.error("Traffic logs fetch error:", err);
    } finally {
      setTrafficLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Active Users ────────────────────────────────────────────────────────────
  const [activeUsersData, setActiveUsersData] = useState({
    active_users_count: 0,
    period_minutes: 15,
    since: null,
    users: [],
  });
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const data = await AbstractAPI.getActiveUsers(15);
      setActiveUsersData(data);
    } catch (err) {
      console.error("Active users fetch error:", err);
    }
  }, []);

const fetchRegistrations = useCallback(async (pg = 1) => {
  setRegistrationLoading(true);
  try {
    const data = await AbstractAPI.getRegistrations(pg, PER_PAGE);
    setRegistrationLogs(data.results);
    setRegistrationCount(data.count);
  } catch (err) {
    console.error("Registration fetch error:", err);
  } finally {
    setRegistrationLoading(false);
  }
}, []);

  // ── Initial Fetch — use sessionStorage only ─────────────────────────────────
  useEffect(() => {
    // Only fetch if a session access token exists (no localStorage)
    if (!sessionStorage.getItem("access")) return;

    fetchLoginLogs(1);
    fetchTrafficLogs(1);
    fetchDownloadLogs(1);
    fetchActiveUsers();
  }, [fetchLoginLogs, fetchTrafficLogs, fetchDownloadLogs, fetchActiveUsers]);

  // ── Refetch on page change (server-side tabs) ───────────────────────────────
  useEffect(() => {
    if (!sessionStorage.getItem("access") || activeTab !== "downloads") return;
    fetchDownloadLogs(downloadPage, fromDate, toDate);
  }, [downloadPage, activeTab]); // eslint-disable-line

  useEffect(() => {
    if (!sessionStorage.getItem("access") || activeTab !== "logins") return;
    fetchLoginLogs(loginPage, fromDate, toDate);
  }, [loginPage, activeTab]); // eslint-disable-line

  useEffect(() => {
    if (!sessionStorage.getItem("access") || activeTab !== "traffic") return;
    fetchTrafficLogs(trafficPage, fromDate, toDate);
  }, [trafficPage, activeTab]); // eslint-disable-line

  useEffect(() => {
  if (!sessionStorage.getItem("access")) return;

  if (activeTab === "Registrations") {
    fetchRegistrations(RegistrationPage);
  }
}, [activeTab, RegistrationPage]);

  // ── Apply date filter (server-side: refetch page 1) ─────────────────────────
  useEffect(() => {
    if (activeTab === "logins") {
      setLoginPage(1);
      fetchLoginLogs(1, fromDate, toDate);
    } else if (activeTab === "traffic") {
      setTrafficPage(1);
      fetchTrafficLogs(1, fromDate, toDate);
    } else if (activeTab === "downloads") {
      setDownloadPage(1);
      fetchDownloadLogs(1, fromDate, toDate);
    } else if (activeTab === "Registrations") {
      fetchRegistrations(1);
    }
  }, [fromDate, toDate]); // eslint-disable-line

  /* ================= Current Table Data ================= */
const baseData =
  activeTab === "downloads"
    ? downloadLogs
    : activeTab === "logins"
    ? loginLogs
    : activeTab === "traffic"
    ? trafficLogs
    : registrationLogs;

  const columns = COLUMNS[activeTab];

  const filteredData = useMemo(() => {
    let data = baseData;

    // 🔹 Filter by type (only for downloads tab)
    if (activeTab === "downloads" && downloadType) {
      data = data.filter(
        (item) =>
          item.download_type?.toLowerCase() === downloadType.toLowerCase(),
      );
    }

    // 🔹 Search filter
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((item) =>
        Object.values(item).join(" ").toLowerCase().includes(q),
      );
    }

    return data;
  }, [baseData, search, downloadType, activeTab]);

  const paginatedData = filteredData; // server-paginated

  const downloadPageCount = Math.ceil(downloadCount / PER_PAGE);
  const loginPageCount = Math.ceil(loginCount / PER_PAGE);
  const trafficPageCount = Math.ceil(trafficCount / PER_PAGE);
const registrationPageCount = Math.ceil(registrationCount / PER_PAGE);
 const isLoading =
  (activeTab === "downloads" && downloadLoading) ||
  (activeTab === "logins" && loginLoading) ||
  (activeTab === "traffic" && trafficLoading) ||
  (activeTab === "Registrations" && registrationLoading);
  /* ================= Handlers ================= */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setPage(1);
    setFromDate("");
    setToDate("");
  };

  const handlePageChange = (_, v) => {
    if (activeTab === "downloads") setDownloadPage(v);
    else if (activeTab === "logins") setLoginPage(v);
    else if (activeTab === "traffic") setTrafficPage(v);
    else if (activeTab === "Registrations") setRegistrationPage(v);
  };

const currentPage =
  activeTab === "downloads"
    ? downloadPage
    : activeTab === "logins"
    ? loginPage
    : activeTab === "traffic"
    ? trafficPage
    : RegistrationPage;

const currentPageCount =
  activeTab === "downloads"
    ? downloadPageCount
    : activeTab === "logins"
    ? loginPageCount
    : activeTab === "traffic"
    ? trafficPageCount
    : registrationPageCount;

  const handleExport = () => {
    exportCSV(filteredData, columns, `${activeTab}-logs.csv`);
  };

  /* ================= UI ================= */
  return (
    <div className="admin-tracking-page">
      {/* Active Users Modal */}
      {showActiveUsersModal && (
        <ActiveUsersModal
          users={activeUsersData.users ?? []}
          count={activeUsersData.active_users_count ?? 0}
          since={activeUsersData.since}
          onClose={() => setShowActiveUsersModal(false)}
        />
      )}

      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>Admin Tracking Dashboard</h1>
          <p>Professional Analytics &amp; Monitoring Panel</p>
        </div>
        <button className="back-btn" onClick={() => navigate("/Admin")}>
          <ArrowLeft size={18} /> Back
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="pro-card" onClick={() => handleTabChange("downloads")}>
          <Download />
          <h3>{downloadCount}</h3>
          <p>Total Downloads</p>
        </div>
        <div className="pro-card" onClick={() => handleTabChange("logins")}>
          <Users />
          <h3>{loginCount}</h3>
          <p>Total Login Events</p>
        </div>
        <div className="pro-card" onClick={() => handleTabChange("traffic")}>
          <FileText />
          <h3>{trafficCount}</h3>
          <p>Total API Hits</p>
        </div>
        {/* Active Users card — click opens detail modal */}
        <div
          className="pro-card pro-card-active"
          onClick={() => setShowActiveUsersModal(true)}
          title="Click to see active user details"
          style={{ cursor: "pointer" }}
        >
          <Activity />
          <h3>{activeUsersData.active_users_count ?? 0}</h3>
          <p>Active Now (last 15 min)</p>
          <small style={{ fontSize: "0.7rem", opacity: 0.7 }}>
            Click to view details
          </small>
        </div>
        <div
          className="pro-card"
          onClick={() => handleTabChange("Registrations")}
        >
          <FileText />
          <h3>{registrationCount}</h3>
          <p>Total Registrations</p>
        </div>
      </div>

      {/* Tab Section Title */}
      <div className="tab-section-title">
        <h2>
          {activeTab === "downloads" && "Download Logs"}
          {activeTab === "logins" && "Login Logs"}
          {activeTab === "traffic" && "Traffic Logs"}
          {activeTab === "Registrations" && "Registration Logs"}
        </h2>
        <span className="tab-count">
          {activeTab === "downloads" && `${downloadCount} records`}
          {activeTab === "logins" && `${loginCount} records`}
          {activeTab === "traffic" && `${trafficCount} records`}
          {activeTab === "Registrations" && `${registrationCount} records`}
        </span>
      </div>

      {/* Date Range Filter */}
      <DateRangeFilter
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={(val) => {
          setFromDate(val);
          setPage(1);
        }}
        onToChange={(val) => {
          setToDate(val);
          setPage(1);
        }}
        onClear={() => {
          setFromDate("");
          setToDate("");
          setPage(1);
        }}
      />

      {/* Search + Export */}
      <div className="filters-bar">
        {activeTab === "downloads" && (
          <select
            value={downloadType}
            onChange={(e) => setDownloadType(e.target.value)}
            className="type-filter"
            style={{ marginLeft: "10px" }}
          >
            <option value="">All Types</option>
            <option value="pdf">PDF</option>
            <option value="ppt">PPT</option>
          </select>
        )}
        <div className="search-box">
          <Search size={16} />
          <input
            placeholder="Search current page..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <button className="export-btn" onClick={handleExport}>
          <ExportIcon size={16} /> Export
        </button>
      </div>

      {/* Table */}
      <div className="table-card">
        {isLoading ? (
          <div className="loading-state">Loading...</div>
        ) : (
          <table className="log-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      textAlign: "center",
                      padding: "2rem",
                      color: "#888",
                    }}
                  >
                    No records found.
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, i) => (
                  <tr key={row.id ?? i}>
                    {columns.map((c) => (
                      <td key={c.key}>
                        {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {currentPageCount > 1 && (
        <div className="pagination-box">
          <Pagination
            count={currentPageCount}
            page={currentPage}
            onChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
}
